import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Shield, Activity, Users, AlertTriangle, CheckCircle, XCircle, Zap, BarChart3, Clock } from 'lucide-react';

const FLAG_LABELS = {
  ratio_engine_enabled: 'Ratio Engine',
  group_session_enabled: 'Group Sessions',
  ratio_validation_enabled: 'Ratio Validation',
  shared_staff_assignment_enabled: 'Shared Staff Assignment',
  centre_session_enabled: 'Centre Sessions',
  group_timesheet_linking_enabled: 'Timesheet Linking',
  group_invoice_linking_enabled: 'Invoice Linking',
};

const FLAG_RISK = {
  ratio_engine_enabled: 'low',
  group_session_enabled: 'low',
  ratio_validation_enabled: 'medium',
  shared_staff_assignment_enabled: 'medium',
  centre_session_enabled: 'medium',
  group_timesheet_linking_enabled: 'high',
  group_invoice_linking_enabled: 'high',
};

const RISK_STYLES = { low: 'bg-emerald-100 text-emerald-700', medium: 'bg-amber-100 text-amber-700', high: 'bg-red-100 text-red-700' };

export default function RolloutDashboardPage() {
  const [monitoring, setMonitoring] = useState(null);
  const [pilotConfig, setPilotConfig] = useState(null);
  const [digestConfig, setDigestConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  // Activation dialog
  const [activatingFlag, setActivatingFlag] = useState(null);
  const [flagValidation, setFlagValidation] = useState(null);
  const [overrideReason, setOverrideReason] = useState('');
  const [activating, setActivating] = useState(false);

  // Simulation
  const [simScenarios, setSimScenarios] = useState([]);
  const [simStatus, setSimStatus] = useState(null);
  const [simRunning, setSimRunning] = useState(false);
  const [simCleaning, setSimCleaning] = useState(false);

  // Validation
  const [validationResult, setValidationResult] = useState(null);
  const [validating, setValidating] = useState(false);

  // Pilot editor
  const [pilotUserInput, setPilotUserInput] = useState('');
  const [pilotEnabled, setPilotEnabled] = useState(false);
  const [pilotUsers, setPilotUsers] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [mRes, pRes, dRes] = await Promise.all([
        api.get('/rollout/monitoring'),
        api.get('/rollout/pilot-config'),
        api.get('/rollout/digest-config'),
      ]);
      setMonitoring(mRes.data.data);
      const pilot = pRes.data.data;
      setPilotConfig(pilot);
      setPilotEnabled(pilot.enabled);
      setPilotUsers(pilot.user_ids || []);
      setDigestConfig(dRes.data.data);
      // Load simulation data
      const [scRes, ssRes] = await Promise.allSettled([
        api.get('/rollout/simulation/scenarios'),
        api.get('/rollout/simulation/status'),
      ]);
      if (scRes.status === 'fulfilled') setSimScenarios(scRes.value.data.data || []);
      if (ssRes.status === 'fulfilled') setSimStatus(ssRes.value.data.data);
    } catch { toast.error('Failed to load rollout data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleToggleFlag = async (flagKey, currentValue) => {
    if (currentValue) {
      // Deactivating
      try {
        await api.post('/rollout/deactivate-flag', { flag_key: flagKey });
        toast.success(`${FLAG_LABELS[flagKey]} deactivated`);
        load();
      } catch (err) { toast.error(err.response?.data?.error?.message || 'Failed'); }
    } else {
      // Pre-validate before showing dialog
      try {
        const res = await api.post('/rollout/validate-flag', { flag_key: flagKey });
        setFlagValidation(res.data.data);
        setActivatingFlag(flagKey);
        setOverrideReason('');
      } catch (err) { toast.error(err.response?.data?.error?.message || 'Failed'); }
    }
  };

  const handleActivate = async (override = false) => {
    setActivating(true);
    try {
      const res = await api.post('/rollout/activate-flag', {
        flag_key: activatingFlag,
        override,
        override_reason: overrideReason || null,
      });
      const d = res.data.data;
      if (d.activated) {
        toast.success(`${FLAG_LABELS[activatingFlag]} activated`);
        setActivatingFlag(null);
        load();
      } else {
        setFlagValidation({ gate: { allowed: true }, health: d.validation });
        toast.error(d.message || 'Activation blocked');
      }
    } catch (err) {
      const msg = err.response?.data?.detail?.error?.message || err.response?.data?.error?.message || 'Failed';
      toast.error(msg);
    } finally { setActivating(false); }
  };

  const handleSavePilot = async () => {
    try {
      await api.put('/rollout/pilot-config', { enabled: pilotEnabled, user_ids: pilotUsers });
      toast.success('Pilot config saved');
      load();
    } catch { toast.error('Failed to save'); }
  };

  const handleSaveDigest = async () => {
    try {
      await api.put('/rollout/digest-config', digestConfig);
      toast.success('Digest config saved');
    } catch { toast.error('Failed to save'); }
  };

  const handleRunSimulation = async () => {
    setSimRunning(true);
    try {
      const allKeys = simScenarios.map(s => s.key);
      const res = await api.post('/rollout/simulation/run', { scenarios: allKeys });
      toast.success(res.data.data.message);
      load();
    } catch (err) { toast.error(err.response?.data?.error?.message || 'Simulation failed'); }
    finally { setSimRunning(false); }
  };

  const handleCleanupSimulation = async () => {
    setSimCleaning(true);
    try {
      const res = await api.post('/rollout/simulation/cleanup');
      toast.success(res.data.data.message);
      load();
    } catch { toast.error('Cleanup failed'); }
    finally { setSimCleaning(false); }
  };

  const handleValidateSystem = async () => {
    setValidating(true);
    try {
      const res = await api.get('/rollout/validate-rollout?hours=72');
      setValidationResult(res.data.data);
      toast.success('Validation complete');
    } catch { toast.error('Validation failed'); }
    finally { setValidating(false); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (!monitoring) return null;

  const m = monitoring;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="rollout-dashboard">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Shield className="h-6 w-6" /> Rollout Control</h1>
        <p className="text-muted-foreground mt-1">Controlled activation, monitoring, and pilot management</p>
      </div>

      {/* Monitoring Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card data-testid="health-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Activity className="h-4 w-4 text-blue-600" /> System Health</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Total Sessions</span><span className="font-mono font-semibold">{m.system_health.total_sessions}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Published</span><span className="font-mono font-semibold">{m.system_health.published_sessions}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Completed</span><span className="font-mono font-semibold">{m.system_health.completed_sessions}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Ratio Failures</span><span className={`font-mono font-semibold ${m.system_health.ratio_failures > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{m.system_health.ratio_failures}</span></div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="safety-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-600" /> Financial Safety</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Manual Review</span><span className={`font-mono font-semibold ${m.financial_safety.manual_review_count > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>{m.financial_safety.manual_review_count}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Exceptions</span><span className={`font-mono font-semibold ${m.financial_safety.exception_count > 0 ? 'text-amber-600' : ''}`}>{m.financial_safety.exception_count}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Pending Links</span><span className="font-mono font-semibold">{m.financial_safety.pending_links}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Total Links</span><span className="font-mono font-semibold">{m.financial_safety.total_links}</span></div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="usage-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Users className="h-4 w-4 text-indigo-600" /> Usage</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Pilot Mode</span><Badge variant={m.usage.pilot_enabled ? 'default' : 'secondary'}>{m.usage.pilot_enabled ? 'Active' : 'Off'}</Badge></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Pilot Users</span><span className="font-mono font-semibold">{m.usage.pilot_users}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Sessions Today</span><span className="font-mono font-semibold">{m.usage.sessions_today}</span></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Feature Flag Control */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Zap className="h-4 w-4" /> Feature Flag Control</CardTitle>
          <CardDescription>Toggle flags safely with sign-off gates and health validation</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Object.entries(FLAG_LABELS).map(([key, label]) => {
              const enabled = m.feature_flags[key] || false;
              const risk = FLAG_RISK[key] || 'low';
              return (
                <div key={key} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`flag-row-${key}`}>
                  <div className="flex items-center gap-3">
                    <Switch checked={enabled} onCheckedChange={() => handleToggleFlag(key, enabled)} data-testid={`flag-toggle-${key}`} />
                    <div>
                      <p className="text-sm font-medium">{label}</p>
                      <p className="text-xs text-muted-foreground font-mono">{key}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={RISK_STYLES[risk]}>{risk}</Badge>
                    {enabled ? <CheckCircle className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-slate-300" />}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Pilot User Config */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4" /> Pilot User Restriction</CardTitle>
          <CardDescription>Limit new features to specific pilot users. When disabled, all admins have access.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <Switch checked={pilotEnabled} onCheckedChange={setPilotEnabled} data-testid="pilot-toggle" />
            <Label>{pilotEnabled ? 'Pilot mode active' : 'Pilot mode off (all admins have access)'}</Label>
          </div>
          {pilotEnabled && (
            <div>
              <Label className="text-xs">Pilot User IDs (comma-separated)</Label>
              <Input value={pilotUsers.join(', ')} onChange={e => setPilotUsers(e.target.value.split(',').map(s => s.trim()).filter(Boolean))} placeholder="user_id_1, user_id_2" data-testid="pilot-users-input" />
              <p className="text-xs text-muted-foreground mt-1">{pilotUsers.length} pilot user(s) configured</p>
            </div>
          )}
          <Button size="sm" onClick={handleSavePilot} data-testid="save-pilot-btn">Save Pilot Config</Button>
        </CardContent>
      </Card>

      {/* Digest Config */}
      {digestConfig && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><Clock className="h-4 w-4" /> Daily Digest Config</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <Switch checked={digestConfig.enabled} onCheckedChange={v => setDigestConfig({...digestConfig, enabled: v})} data-testid="digest-toggle" />
              <Label>{digestConfig.enabled ? 'Scheduled digest active' : 'Digest disabled'}</Label>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Send Time</Label>
                <Input type="time" value={digestConfig.send_time || '07:00'} onChange={e => setDigestConfig({...digestConfig, send_time: e.target.value})} data-testid="digest-time" />
              </div>
              <div>
                <Label className="text-xs">Timezone</Label>
                <Input value={digestConfig.timezone || 'Australia/Sydney'} onChange={e => setDigestConfig({...digestConfig, timezone: e.target.value})} placeholder="Australia/Sydney" data-testid="digest-timezone" />
              </div>
              <div className="flex items-end">
                <Button size="sm" onClick={handleSaveDigest} data-testid="save-digest-btn">Save Config</Button>
              </div>
            </div>
            {digestConfig.last_sent && <p className="text-xs text-muted-foreground">Last sent: {digestConfig.last_sent}</p>}
          </CardContent>
        </Card>
      )}

      {/* Pre-Live Simulation */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2"><Zap className="h-4 w-4" /> Pre-Live Simulation</CardTitle>
              <CardDescription>Generate test scenarios to validate system behavior</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {simStatus?.has_simulation_data && (
                <Button size="sm" variant="destructive" onClick={handleCleanupSimulation} disabled={simCleaning} data-testid="cleanup-sim-btn">
                  {simCleaning ? 'Cleaning...' : `Cleanup (${simStatus.total})`}
                </Button>
              )}
              <Button size="sm" onClick={handleRunSimulation} disabled={simRunning} data-testid="run-sim-btn">
                {simRunning ? 'Running...' : 'Run All Scenarios'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {simScenarios.map(s => (
              <div key={s.key} className="p-2 border rounded-lg text-center" data-testid={`sim-scenario-${s.key}`}>
                <p className="text-xs font-medium">{s.label}</p>
                <p className="text-[10px] text-muted-foreground">P:{s.participants} S:{s.staff}</p>
              </div>
            ))}
          </div>
          {simStatus?.has_simulation_data && (
            <div className="mt-3 p-2 bg-amber-50 rounded-lg text-xs text-amber-700">
              Simulation data present: {simStatus.counts.group_sessions} sessions, {simStatus.counts.group_session_participants} participants, {simStatus.counts.group_session_staff} staff
            </div>
          )}
        </CardContent>
      </Card>

      {/* Validate System */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2"><BarChart3 className="h-4 w-4" /> System Validation</CardTitle>
              <CardDescription>Quick validation checkpoint using recent report data</CardDescription>
            </div>
            <Button size="sm" onClick={handleValidateSystem} disabled={validating} data-testid="validate-system-btn">
              <CheckCircle className="h-4 w-4 mr-2" /> {validating ? 'Validating...' : 'Validate System'}
            </Button>
          </div>
        </CardHeader>
        {validationResult && (
          <CardContent>
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">Last {validationResult.period_hours} hours | Validated {new Date(validationResult.validation_time).toLocaleString()}</p>
              <div className="grid grid-cols-3 gap-3">
                {Object.entries(validationResult.reports || {}).map(([key, val]) => (
                  <div key={key} className="p-3 border rounded-lg text-center">
                    <p className="text-xs text-muted-foreground">{val.title}</p>
                    <p className="text-lg font-mono font-bold">{val.total}</p>
                    <p className="text-[10px] text-muted-foreground">records</p>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Activation Dialog */}
      <Dialog open={!!activatingFlag} onOpenChange={() => setActivatingFlag(null)}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Zap className="h-5 w-5" /> Activate {FLAG_LABELS[activatingFlag]}</DialogTitle>
            <DialogDescription>Review validation results before enabling this feature flag.</DialogDescription>
          </DialogHeader>
          {flagValidation && (
            <div className="space-y-4 py-4">
              {/* Gate check */}
              <div className={`p-3 rounded-lg border ${flagValidation.gate.allowed ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                <div className="flex items-center gap-2">
                  {flagValidation.gate.allowed ? <CheckCircle className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-red-600" />}
                  <span className="text-sm font-medium">Sign-off Gate: {flagValidation.gate.allowed ? 'Passed' : 'Blocked'}</span>
                </div>
                {flagValidation.gate.pending_roles && (
                  <p className="text-xs text-red-600 mt-1">Pending: {flagValidation.gate.pending_roles.join(', ')}</p>
                )}
              </div>

              {/* Health check */}
              <div className={`p-3 rounded-lg border ${flagValidation.health.safe ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                <div className="flex items-center gap-2">
                  {flagValidation.health.safe ? <CheckCircle className="h-4 w-4 text-emerald-600" /> : <AlertTriangle className="h-4 w-4 text-amber-600" />}
                  <span className="text-sm font-medium">Health: {flagValidation.health.safe ? 'Clear' : `${flagValidation.health.warnings.length} warning(s)`}</span>
                </div>
                {flagValidation.health.warnings.map((w, i) => (
                  <p key={i} className="text-xs text-amber-700 mt-1">{w}</p>
                ))}
              </div>

              {flagValidation.health.requires_override && (
                <div>
                  <Label>Override Reason (required)</Label>
                  <Textarea value={overrideReason} onChange={e => setOverrideReason(e.target.value)} rows={2} placeholder="Explain why activation is safe..." data-testid="activation-override-reason" />
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setActivatingFlag(null)}>Cancel</Button>
            {flagValidation?.gate.allowed && (
              <Button
                onClick={() => handleActivate(flagValidation?.health.requires_override)}
                disabled={activating || (flagValidation?.health.requires_override && overrideReason.trim().length < 5)}
                data-testid="confirm-activate-btn"
              >
                {activating ? 'Activating...' : (flagValidation?.health.requires_override ? 'Override & Activate' : 'Activate')}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
